import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { Phone, ShoppingBag, User } from 'lucide-react';
import Logo1 from './assets/download.svg';
import burger_icon from './assets/icon/burger_left.svg';
import { useNavigate, useLocation } from 'react-router-dom';
import { getState } from './Firebase/auth';
import MobileNavBar from './MobileNavbar';
import Patti_home from './Homepage/Home/patti';
import SidebarNav from './navslider';
import ShopBrand from './Business/ShopBrand';
import { motion, useScroll, useTransform, cubicBezier, useMotionValue, useSpring } from 'framer-motion';

export default function NavBar({ flag = 1, style = {} }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024);
  const logoRef = useRef(null);

  const { scrollY } = useScroll();
  const smoothScrollY = useSpring(scrollY, { damping: 20, stiffness: 90 });
  const footerLogoOpacity = useMotionValue(1);

  useEffect(() => {
    const handleFooterLogoVisibility = (e) => {
      footerLogoOpacity.set(1 - e.detail.ratio);
    };
    window.addEventListener('footer-logo-visibility', handleFooterLogoVisibility);
    return () => {
      window.removeEventListener('footer-logo-visibility', handleFooterLogoVisibility);
    };
  }, []);

  const isHome = location.pathname === '/';
  const [homeLogoStartScale, setHomeLogoStartScale] = useState(() => {
    return 1;
  });

  const logoScale = useTransform(smoothScrollY, [0, 300], [homeLogoStartScale, 1]);
  const logoY = useTransform(smoothScrollY, [0, 300], ['0vh', '0vh']);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024);
      setHomeLogoStartScale(1);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const [sidebar, setSideBar] = useState(false);

  let oldScrollY = 0;
  const [isSticky, setIsSticky] = useState(false);

  const [Style, setStyle] = useState({
    background: 'transparent',
    position: 'fixed',
    width: '100%',
    zIndex: 999,
    paddingTop: '15px',
    paddingBottom: '15px',
  });

  const controlDirection = () => {
    if (flag === 3) return;
    if (!isMobile && window.scrollY > 500 && window.scrollY > oldScrollY) {
      setIsSticky(true);
    } else {
      setIsSticky(false);
    }
    oldScrollY = window.scrollY;
  };

  useEffect(() => {
    if (isSticky) {
      setStyle({
        background: 'black',
        position: 'fixed',
        width: '100%',
        zIndex: 999,
        paddingTop: '15px',
        paddingBottom: '15px',
      });
    } else {
      setStyle({
        background: 'transparent',
        position: 'fixed',
        width: '100%',
        zIndex: 999,
        paddingTop: '15px',
        paddingBottom: '15px',
      });
    }
  }, [isSticky]);

  // flag 5 (product pages): pinned to the top, slides away while reading
  // down and comes straight back on any scroll up, so cart and menu are
  // never more than a flick away. flag 1 scrolled off with the page and only
  // returned at the very top.
  // Every page but the home page (flag 3) now behaves this way.
  const peeks = flag === 1 || flag === 5;
  const [peekHidden, setPeekHidden] = useState(false);
  useEffect(() => {
    if (!peeks) return undefined;
    let last = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        if (y < 80) setPeekHidden(false);
        else if (y > last + 6) setPeekHidden(true);
        else if (y < last - 6) setPeekHidden(false);
        last = y;
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      delete document.documentElement.dataset.navHidden;
      document.documentElement.style.removeProperty('--nav-offset');
    };
  }, [peeks]);
  // Lets sticky bars below the navbar move up into its place while it hides.
  // --nav-offset: how far down sticky parts of a page must sit to clear it.
  useEffect(() => {
    if (!peeks) return;
    const hidden = peekHidden && !sidebar;
    document.documentElement.dataset.navHidden = hidden ? '1' : '0';
    document.documentElement.style.setProperty('--nav-offset', hidden ? '0px' : '60px');
  }, [peeks, peekHidden, sidebar]);

  useEffect(() => {
    window.addEventListener('scroll', controlDirection);
    return () => {
      window.removeEventListener('scroll', controlDirection);
    };
  }, []);

  const navBackground = useTransform(scrollY, [0, 1000], ['rgba(0, 0, 0, 0)', 'rgba(0, 0, 0, 1)'], { ease: cubicBezier(0.5, 0, 0, 1) });
  const navContentOpacity = useTransform(scrollY, [0, 100], [1, 1], { ease: cubicBezier(0.5, 0, 0, 1) });
  const pattiHeight = useTransform(scrollY, [0, 200], ['35px', '0px'], { ease: cubicBezier(0.5, 0, 0, 1) });
  const pattiOpacity = useTransform(scrollY, [0, 200], [1, 0], { ease: cubicBezier(0.5, 0, 0, 1) });
  const navPadding = useTransform(scrollY, [0, 200], ['45px', '15px'], { ease: cubicBezier(0.5, 0, 0, 1) });

  const accountClick = () => {
    if (getState()) {
      navigate('/profile');
    } else {
      navigate('/login');
    }
  };

  return (
    <motion.nav
      initial="visible"
      whileInView="visible"
      viewport={{ once: false }}
      transition={{ duration: 0.5 }}
      variants={{ visible: { opacity: 1 }, hidden: { opacity: 0 } }}
      className="NavBar"
      style={
        flag === 3
          ? {
              backgroundColor: navBackground,
              position: 'fixed',
              top: '0px',
              width: '100%',
              zIndex: 999,
              paddingTop: '0px',
              paddingBottom: '0px',
            }
          : flag === 0
          ? Style
          : flag === 2
          ? { position: 'fixed', width: '100%', zIndex: 999, paddingTop: '15px', paddingBottom: '15px' }
          : flag === 4
          ? { position: 'relative', width: '100%', zIndex: 999, paddingTop: '15px', paddingBottom: '15px' }
          : peeks
          ? {
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              zIndex: 999,
              paddingTop: '15px',
              paddingBottom: '15px',
              background: '#000',
              transform: peekHidden && !sidebar ? 'translateY(-100%)' : 'translateY(0)',
              transition: 'transform .32s cubic-bezier(.2,.7,.2,1)',
              willChange: 'transform',
            }
          : {}
      }
    >
      <div className="nav_wrapper" style={sidebar ? { display: 'block', opacity: '1' } : {}} onClick={() => setSideBar(false)}></div>
      <SidebarNav sidebar={sidebar} setSideBar={setSideBar} />

      {flag === 3 && !isSticky && (
        <motion.div style={{ height: pattiHeight, opacity: pattiOpacity, overflow: 'hidden' }}>
          <Patti_home />
        </motion.div>
      )}
      <motion.div className="Navbody txt-font" style={flag === 3 ? { paddingTop: isMobile ? '0px' : navPadding, paddingBottom: isMobile ? '0px' : navPadding } : {}}>
        {isMobile ? (
          <MobileNavBar sidebar={sidebar} setSideBar={setSideBar} footerLogoOpacity={footerLogoOpacity} />
        ) : (
          <div className="nav-desktop max-w-[98%] xl:max-w-[1600px] mx-auto" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <motion.div className="nav-left txt-font1" style={isHome ? { opacity: navContentOpacity } : {}}>
              <div type="button">
                <img src={burger_icon} alt="Open menu" role="button" tabIndex={0} style={{ width: '30px' }} className="sidenav_burger" onClick={() => setSideBar(!sidebar)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSideBar(!sidebar)} />
              </div>
            </motion.div>

            <div style={{ position: 'relative', zIndex: 1000 }}>
              <a href="/" style={{ textDecoration: 'none', display: 'block' }}>
                {isHome ? (
                  <ShopBrand motionStyle={{ transformOrigin: 'center', scale: logoScale, y: logoY }}>
                    <img src={Logo1} alt="Logo" className="logo_main" />
                  </ShopBrand>
                ) : (
                  <ShopBrand>
                    <img
                      src={Logo1}
                      alt="Logo"
                      className="logo_main"
                      style={{ display: 'block', opacity: 1, width: '240px', height: 'auto' }}
                    />
                  </ShopBrand>
                )}
              </a>
            </div>

            <motion.div className="nav-right txt-font1" style={isHome ? { opacity: navContentOpacity } : {}}>
              <a href="/reachus/general" style={{ textDecoration: 'none' }} aria-label="Contact us">
                <Phone className="nav-icon text-white w-6 h-6" strokeWidth={1.5} />
              </a>
              <a href="/cart" style={{ textDecoration: 'none' }} aria-label="Your bag">
                <ShoppingBag className="nav-icon text-white w-6 h-6" strokeWidth={1.5} />
              </a>
              {/* A real href, so the link is crawlable; the click still decides
                  between the profile and the login page. */}
              <a
                href="/profile"
                style={{ textDecoration: 'none', cursor: 'pointer' }}
                aria-label="Your account"
                onClick={(e) => {
                  e.preventDefault();
                  accountClick();
                }}
              >
                <User className="nav-icon text-white w-6 h-6" strokeWidth={1.5} />
              </a>
            </motion.div>
          </div>
        )}
      </motion.div>
    </motion.nav>
  );
}
